import { useTranslation } from "react-i18next";
import {
  AutoCompleteMulti,
  FormContainer,
  FormEmojiTextInput,
  FormInput,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import {
  automationReceivedCustomMessageSchema,
  TAutomationReceivedCustomMessageForm,
} from "./AutomationReceivedCustomMessageSchema";
import {
  createTicketAutomationReceivedCustomMessage,
  getTicketAutomationReceivedCustomMessageById,
  updateTicketAutomationReceivedCustomMessageById,
} from "../../../../../../../services/automationReceivedCustomMessage.service";
import { BoardList } from "./BoardList/BoardList";
import { IAutomationReceivedCustomMessageProps } from "./models";
import { calendarExamGroupingWebList } from "../../../../../../../services/calendarExamGroupingWeb.service";
import { calendarRegisterList } from "../../../../../../../services/ticketAutomationReceivedSlotCalendar.service";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedCustomMessage.";

const AutomationReceivedCustomMessage = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
  automationCode,
}: IAutomationReceivedCustomMessageProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedCustomMessage
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedCustomMessageById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedCustomMessageById
  );

  const [
    optionsTicketAutomationReceivedCaleExamGroupings,
    setTicketAutomationReceivedCaleExamGroupings,
  ] = useState<ISelectType[]>([]);

  const { sendRequest: proceduresRequest } = useFetch(
    calendarExamGroupingWebList
  );

  const [
    optionsTicketAutomationReceivedCustomMessageCalendars,
    setTicketAutomationReceivedCustomMessageCalendars,
  ] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(calendarRegisterList);

  const updateHandler = (v: TAutomationReceivedCustomMessageForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        ticketChannelStepId: v.ticketChannelStep
          ? v.ticketChannelStep.id
          : undefined,
        message: v.message,
        ticketAutomationReceivedCaleExamGroupings:
          v.ticketAutomationReceivedCaleExamGroupings
            ? v.ticketAutomationReceivedCaleExamGroupings.map((cal) => ({
                externalId: cal.id,
                externalName: cal.name,
              }))
            : undefined,
        ticketAutomationReceivedCustomMessageCalendars:
          v.ticketAutomationReceivedCustomMessageCalendars
            ? v.ticketAutomationReceivedCustomMessageCalendars.map((cal) => ({
                externalId: cal.id,
                externalName: cal.name,
              }))
            : undefined,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TAutomationReceivedCustomMessageForm) =>
    save({
      ...v,
      ticketChannelStepId: v.ticketChannelStep
        ? v.ticketChannelStep.id
        : undefined,
      message: v.message,
      ticketAutomationReceivedCaleExamGroupings:
        v.ticketAutomationReceivedCaleExamGroupings
          ? v.ticketAutomationReceivedCaleExamGroupings.map((cal) => ({
              externalId: cal.id,
              externalName: cal.name,
            }))
          : undefined,
      ticketAutomationReceivedCustomMessageCalendars:
        v.ticketAutomationReceivedCustomMessageCalendars
          ? v.ticketAutomationReceivedCustomMessageCalendars.map((cal) => ({
              externalId: cal.id,
              externalName: cal.name,
            }))
          : undefined,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (
      id: string
    ): Promise<Treturn<TAutomationReceivedCustomMessageForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TAutomationReceivedCustomMessageForm = {
          ...data,
          ticketChannelStep: data.ticketChannelStep
            ? {
                id: data.ticketChannelStep.id,
                name: data.ticketChannelStep.name,
              }
            : undefined,
          ticketChannel: data.ticketChannelStep
            ? {
                id: data.ticketChannelStep.ticketChannel.id,
                name: data.ticketChannelStep.ticketChannel.name,
                code: data.ticketChannelStep.ticketChannel.code,
              }
            : undefined,
          ticketAutomationReceivedCaleExamGroupings:
            data.ticketAutomationReceivedCaleExamGroupings
              ? data.ticketAutomationReceivedCaleExamGroupings.map((cal) => ({
                  id: cal.externalId,
                  name: cal.externalName,
                }))
              : undefined,
          ticketAutomationReceivedCustomMessageCalendars:
            data.ticketAutomationReceivedCustomMessageCalendars
              ? data.ticketAutomationReceivedCustomMessageCalendars.map(
                  (cal) => ({
                    id: cal.externalId,
                    name: cal.externalName,
                  })
                )
              : undefined,
          message: data.message,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  const paramsProcedure = useMemo(() => {
    return {
      EnableCode: true,
    };
  }, []);

  const fetchTicketAutomationReceivedCaleExamGroupings =
    useCallback(async () => {
      if (automationCode !== "21") return;
      const { data, success } = await proceduresRequest(paramsProcedure);
      if (data && success) {
        setTicketAutomationReceivedCaleExamGroupings(
          data.map((d) => ({
            id: d.identifier.toString(),
            name: d.name,
          }))
        );
      }
    }, [proceduresRequest]);

  useEffect(() => {
    fetchTicketAutomationReceivedCaleExamGroupings();
  }, [fetchTicketAutomationReceivedCaleExamGroupings]);

  const params = useMemo(() => {
    return {
      EnableCode: true,
      TasyScheduleTypeCode: "3",
    };
  }, []);

  const fetchTicketAutomationReceivedDaysAheadCalendars =
    useCallback(async () => {
      if (automationCode === "21") return;
      const { data, success } = await sendRequest(params);

      if (data && success) {
        setTicketAutomationReceivedCustomMessageCalendars(
          data.map((d) => {
            const titleBase = d.descriptionSite?.trim() || d.calendarName;

            return {
              id: String(d.identifier),
              name: `${titleBase} - ${d.identifier}`,
            };
          })
        );
      }
    }, [sendRequest, params]);

  useEffect(() => {
    fetchTicketAutomationReceivedDaysAheadCalendars();
  }, [fetchTicketAutomationReceivedDaysAheadCalendars]);
  return (
    <FormContainer<TAutomationReceivedCustomMessageForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "custom_message")}
      schema={automationReceivedCustomMessageSchema}
      subtitle={t(translationPath + "add_custom_message")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <BoardList
            boardFieldName="ticketChannel"
            listFieldName="ticketChannelStep"
          />
          {automationCode && ["21"].includes(automationCode) && (
            <Grid item xs={12} sm={6}>
              <AutoCompleteMulti
                fullWidth
                options={optionsTicketAutomationReceivedCaleExamGroupings}
                name="ticketAutomationReceivedCaleExamGroupings"
                label={t(
                  translationPath + "ticketAutomationReceivedCaleExamGroupings"
                )}
                getOptionLabel={(option) => option.name}
                size="small"
                keepOpenOnSelect={true}
              />
            </Grid>
          )}

          {automationCode && !["21"].includes(automationCode) && (
            <Grid item xs={12} sm={6}>
              <AutoCompleteMulti
                params={params}
                fullWidth
                options={optionsTicketAutomationReceivedCustomMessageCalendars}
                name="ticketAutomationReceivedCustomMessageCalendars"
                label={t(
                  translationPath +
                    "ticketAutomationReceivedCustomMessageCalendars"
                )}
                getOptionLabel={(option) => option.name}
                size="small"
                keepOpenOnSelect={true}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <FormEmojiTextInput
              name="message"
              label={t(translationPath + "message") || "Message"}
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default AutomationReceivedCustomMessage;
